import { useEffect, useState } from "react";
import Button from "@/common/elements/Button";
import Select from "@/common/elements/InputElements/Select";
import Switch from "@/common/elements/InputElements/Switch";
import TextField from "@/common/elements/InputElements/TextField";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import { StyledGridCol, StyledGridRow, ActionHeader } from "@/common/styles";
import Table from "@/common/elements/Table";
import CommonDatePicker from "@/common/elements/DatePicker";
import { Form } from "antd";
import dayjs from "dayjs";
import { v4 as uuidV4 } from "uuid";

import {
  FlagBody,
  FlagFooter,
  FlagHeader,
  FlagSection,
  StyledFlagFooter,
} from "./styles";
import { getFlagIcon, INMATE_FLAG_TYPE } from "../InmateFlag";
import {
  getOptionsFromRefList,
  getRefListItemsbyName,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import { useDispatch, useSelector } from "react-redux";
import { setBookings } from "@/slices/bookingsSlice";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { MODULES_LIST_WITH_ID } from "@/util/common_variable";

const FolderFlags = ({ startingPoint = "" }) => {
  const [form] = Form.useForm();
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const [flagsEnabled, setFlagsEnabled] = useState({});

  const [flagDetails, setFlagDetails] = useState({});
  const [flagTypes, setFlagTypes] = useState([]);

  const flagPlaceholders = {
    FLAGTYPES_GANG: "Select Gang Type",
    FLAGTYPES_DANGER: "Select Danger Type",
    FLAGTYPES_MEDICAL: "Select Medical Type",
    FLAGTYPES_NOTES: "Select Note Type",
  };

  const flagTypeConstants = [
    "FLAGTYPES_GANG",
    "FLAGTYPES_DANGER",
    "FLAGTYPES_MEDICAL",
    "FLAGTYPES_NOTES",
  ];

  const reduxFieldMapping = {
    FLAGTYPES_GANG: "gangTypeId",
    FLAGTYPES_DANGER: "dangerTypeId",
    FLAGTYPES_MEDICAL: "medicalTypeId",
    FLAGTYPES_NOTES: "noteTypeId",
  };

  const { bookings = { cdInvolvements: [] }, bookingsFieldsEnabled = false } =
    useSelector(
      (state) => state.bookings || { bookings: { cdInvolvements: [] } }
    );

  const dispatch = useDispatch();
  const fullSearch = useSelector(state => state.fullSearch);

  useEffect(() => {
    let filterFT = getRefListItemsbyName("FlagTypes")?.filter((f) =>
      flagTypeConstants.includes(f?.constantName)
    );
    console.log(filterFT);

    if (isNonEmptyVariable(startingPoint) && startingPoint === "visitor") {
      filterFT = filterFT?.filter(
        (x) =>
          x?.refListItemGlobalId === 50030003 ||
          x?.refListItemGlobalId === 50030007
      );
    }
    setFlagTypes(filterFT);
  }, [startingPoint]);

  const onFlagsEnableChange = ({ target: { checked, name } }) => {
    form.resetFields();

    setFlagsEnabled({ [name]: checked }); // Enable only one flag at a time

    if (checked) {
      setFlagDetails({
        [name]: {
          id: uuidV4(),
          type: "",
          description: "",
          invExpireDate: null,
        },
      });
    } else {
      setFlagDetails((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const onFlagDetailsChange = (flagType, key, value) => {
    setFlagDetails((prev) => ({
      ...prev,
      [flagType]: { ...prev[flagType], [key]: value },
    }));
  };

  const onAddFlag = (flagType) => {
    form
      .validateFields([`${flagType}_type`, `${flagType}_description`])
      .then(() => {
        const detail = flagDetails[flagType];
        if (!detail || !detail.type) return;

        const flagRef = flagTypes.find((f) => f.constantName === flagType);
        const flagTypeID = flagRef ? flagRef.refListItemGlobalId : null;

        const newInvolvement = {
          invCreatedBy: fullSearch ? null : userDetails?.staffId,
          invObjType: 3,
          invObjId: bookings.bookingId,
          invCreatedDate: fullSearch ? null : dayjs().format("MM/DD/YYYY HH:mm:ss"),
          linkedObjectType: "Jackets",
          relatedObjectType: "Bookings",
          flagTypeID: flagTypeID,
          invExpireDate: detail.invExpireDate
            ? dayjs(detail.invExpireDate).format("MM/DD/YYYY")
            : null,
          [reduxFieldMapping[flagType]]: detail.type || null,
          invDesc: detail.description,
        };

        const updatedBookings = bookings
          ? { ...bookings }
          : { cdInvolvements: [] };

        dispatch(
          setBookings({
            ...updatedBookings,
            cdInvolvements: [
              ...(updatedBookings.cdInvolvements || []),
              newInvolvement,
            ],
          })
        );

        form.resetFields([
          `${flagType}_type`,
          `${flagType}_description`,
          `${flagType}_expireDate`,
        ]);
        setFlagDetails((prev) => ({
          ...prev,
          [flagType]: {
            id: uuidV4(),
            type: "",
            description: "",
            invExpireDate: null,
          },
        }));
      })
      .catch(() => {});
  };

  const renderFlagInstance = (flagType, refListName) => {
    const flag = flagDetails[flagType];
    if (!flag) return null;
    const adjustedRefListName =
      refListName === "NOTES_Type" ? "NOTE_Type" : refListName;

    return (
      <Form form={form}>
        <FlagBody key={flag.id}>
          <StyledGridRow gutter={16}>
            <StyledGridCol lg={6}>
              <FormItem
                label={flagPlaceholders[flagType].replace("Select ", "")}
                name={`${flagType}_type`}
                rules={!fullSearch ? [{ required: true, message: "Flag Type is required" }] : []}
              >
                <Select
                  options={getOptionsFromRefList(
                    adjustedRefListName,
                    false,
                    false,
                    true
                  )}
                  placeholder={flagPlaceholders[flagType] || "Select Type"}
                  showSearch={true}
                  variant="outlined"
                  value={flag.type}
                  disabled={!bookingsFieldsEnabled}
                  onChange={(value) =>
                    onFlagDetailsChange(flagType, "type", value)
                  }
                />
              </FormItem>
            </StyledGridCol>
          </StyledGridRow>

          <StyledGridRow>
            <StyledGridCol lg={12} style={{ marginRight: "20px" }}>
              <FormItem label="Description" name={`${flagType}_description`}>
                <TextField
                  id={`${flagType}Description`}
                  disabled={!bookingsFieldsEnabled}
                  as="textarea"
                  placeholder="Enter Description"
                  value={flag.description}
                  rows={5}
                  onChange={(event) =>
                    onFlagDetailsChange(
                      flagType,
                      "description",
                      event.target.value
                    )
                  }
                />
              </FormItem>
            </StyledGridCol>

            {flagType === "FLAGTYPES_NOTES" && (
              <StyledGridCol lg={6}>
                <FormItem label="Expire Date">
                  <CommonDatePicker
                    placeholder="MM/DD/YYYY"
                    format="MM/DD/YYYY"
                    showTime={false}
                    value={flag.invExpireDate}
                    onChange={(date) =>
                      onFlagDetailsChange(flagType, "invExpireDate", date)
                    }
                  />
                </FormItem>
              </StyledGridCol>
            )}
          </StyledGridRow>

          <FlagFooter>
            <Button
              onClick={() => onAddFlag(flagType)}
              disabled={!flag.type || !bookingsFieldsEnabled}
            >
              Add
            </Button>
          </FlagFooter>
        </FlagBody>
      </Form>
    );
  };

  const renderFlagSection = (flagType, label, refListName = "") => {
    return (
      <FlagSection key={flagType}>
        <FlagHeader className={flagType} enabled={true}>
          <div>
            <img
              alt={`flag ${INMATE_FLAG_TYPE[flagType]} icon`}
              src={getFlagIcon(flagType)}
            />{" "}
            &nbsp;
            {label}
          </div>
          <Switch
            id={flagType}
            name={flagType}
            value={flagsEnabled?.[flagType]}
            onChange={onFlagsEnableChange}
            disabled={!bookingsFieldsEnabled}
          />
        </FlagHeader>
        {flagsEnabled?.[flagType] &&
          renderFlagInstance(flagType, `${flagType.split("_")[1]}_Type`)}
      </FlagSection>
    );
  };

  const tableData = {
    headers: [
      { key: "flagName", text: "Flag Name" },
      { key: "type", text: "Type" },
      { key: "invDesc", text: "Description" },
      { key: "invCreatedBy", text: "Created By" },
    ],
    rows: (bookings?.cdInvolvements || [])
      .filter((inv) => (inv.relatedObjectType === "Bookings" || inv.relatedObjectType  === "VisitorLog") && inv.flagTypeID)
      .map((inv, index) => {
        const flagRef = flagTypes.find(
          (f) => f.refListItemGlobalId === inv.flagTypeID
        );

        if (!flagRef) return null;

        const flagName = flagRef ? flagRef.longString : "-";
        let typeLabel = "-";
        if (flagRef) {
          const reduxField = reduxFieldMapping[flagRef.constantName];
          let refListName = `${flagRef.constantName.split("_")[1]}_Type`;
          if (refListName === "NOTES_Type") {
            refListName = "NOTE_Type";
          }
          if (reduxField && inv[reduxField]) {
            const options = getOptionsFromRefList(
              refListName,
              false,
              false,
              true
            );
            const found = options.find((opt) => opt.value === inv[reduxField]);
            if (found) {
              typeLabel = found.label;
            }
          }
        }
        const staffOptions = getOptionsFromRefList("", true);
        const createdByLabel =
          staffOptions.find((opt) => opt.value === inv.invCreatedBy)?.label ||
          inv.invCreatedBy;
        inv.invCreatedBy === userDetails?.staffId
          ? userDetails?.username
          : staffOptions.find((opt) => opt.value === inv.invCreatedBy)?.label ||
            inv.invCreatedBy;

        return {
          id: index + 1,
          flagName,
          type: typeLabel,
          invDesc: inv.invDesc || "-",
          invCreatedBy: createdByLabel,
        };
      })
      .filter(Boolean),
  };

  return (
    <>
      {/* {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.module,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS
      ) && ( */}
      <>
        {flagTypes?.map((f) =>
          renderFlagSection(
            f.constantName,
            f.longString,
            `${f.constantName.split("_")[1]}_Type`
          )
        )}

        <Table
          headers={tableData.headers}
          data={tableData.rows}
          style={{ marginTop: "15px" }}
        />
      </>
      {/* )} */}
    </>
  );
};

export default FolderFlags;
